/**
 * План v2 — об'єднаний редактор плану + аналіз оптимізатора в одному рядку.
 *
 * Колонки (на годину):
 *   1. Год
 *   2. Ціна РДН (з бейджем «прогноз»)
 *   3. P (slider) — потужність КГУ
 *   4. V_eko (input) — обсяг для ЕкоСфери
 *   5. Прогноз ЕкоСфера (input, з client_forecasts)
 *   6. V_rdn (readonly) = free − V_eko
 *   7. Ваш profit (live, з /optimizer/quick-calc)
 *   8. Best (P / profit) — з /optimizer/{date}
 *   9. Δ profit — your − best
 *   10. Save кнопка / expand chevron
 *
 * Логіка:
 *   - power_min/max/step з kgu_params
 *   - self_needs з kgu_params.self_needs_estimate_kw
 *   - Toggle «Власні потреби» (header, default ON, localStorage)
 *   - free = P - (toggle ? self : 0)
 *   - V_eko capped to free (deficit warning)
 *   - V_rdn = free - V_eko (auto, readonly)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Copy,
  Save,
  Send,
  Sparkles,
} from "lucide-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { clientsApi } from "@/api/clients";
import { forecastsApi } from "@/api/forecasts";
import {
  type DayOptimum,
  optimizerApi,
  type QuickCalcOut,
} from "@/api/optimizer";
import { parametersApi } from "@/api/parameters";
import { plansApi } from "@/api/plans";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCanEdit } from "@/hooks/useRole";
import { fmt, num, todayIso } from "@/lib/format";
import { cn } from "@/lib/utils";

interface RowDraft {
  power_kw: string;
  v_eko: string;
  forecast: string;
}

function shiftDate(iso: string, deltaDays: number): string {
  const d = new Date(iso);
  d.setDate(d.getDate() + deltaDays);
  return d.toISOString().slice(0, 10);
}

const SELF_NEEDS_TOGGLE_KEY = "plan2-count-self-needs";

export function PlanV2Page() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canEdit = useCanEdit();

  const [planDate, setPlanDate] = useState(todayIso());
  const [countSelfNeeds, setCountSelfNeeds] = useState<boolean>(
    () => localStorage.getItem(SELF_NEEDS_TOGGLE_KEY) !== "0",
  );
  useEffect(() => {
    localStorage.setItem(SELF_NEEDS_TOGGLE_KEY, countSelfNeeds ? "1" : "0");
  }, [countSelfNeeds]);

  // ─── Data loads ───
  const planQ = useQuery({
    queryKey: ["plans", planDate],
    queryFn: () => plansApi.get(planDate),
  });
  const optQ = useQuery({
    queryKey: ["optimizer", planDate],
    queryFn: () => optimizerApi.day(planDate),
    retry: 0,
  });
  const kguQ = useQuery({
    queryKey: ["kgu-params", planDate],
    queryFn: () => parametersApi.getKguParams(planDate),
    retry: 0,
  });
  const forecastQ = useQuery({
    queryKey: ["forecasts", planDate],
    queryFn: () => forecastsApi.list(planDate),
  });
  const baselineEkoQ = useQuery({
    queryKey: ["forecasts-baseline", planDate, "ekosfera"],
    queryFn: () => forecastsApi.baseline(planDate, "ekosfera"),
    retry: 0,
  });
  const baselineEkoByHour = useMemo(() => {
    const map = new Map<number, number>();
    const data = baselineEkoQ.data?.by_hour;
    if (!data) return map;
    for (const [k, v] of Object.entries(data)) {
      const n = typeof v === "string" ? Number(v) : v;
      if (Number.isFinite(n)) map.set(Number(k), n);
    }
    return map;
  }, [baselineEkoQ.data]);
  const clientsQ = useQuery({
    queryKey: ["clients", { includeInactive: false }],
    queryFn: () => clientsApi.list(false),
  });

  const powerMin = Number(kguQ.data?.power_min_kw ?? 600);
  const powerMax = Number(kguQ.data?.power_max_kw ?? 1500);
  const powerStep = Number(kguQ.data?.power_step_kw ?? 10);
  const selfNeedsKw = Number(kguQ.data?.self_needs_estimate_kw ?? 20);
  const selfNeedsApplied = countSelfNeeds ? selfNeedsKw : 0;

  const submitted = !!planQ.data?.submitted_at;
  const readonly = !canEdit || submitted;

  const ekoClient = useMemo(
    () => (clientsQ.data ?? []).find((c) => c.code === "ekosfera"),
    [clientsQ.data],
  );

  // ─── Local draft state ───
  const [rows, setRows] = useState<Record<number, RowDraft>>({});
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  useEffect(() => {
    if (planQ.data === undefined || forecastQ.data === undefined) return;
    if (loadedFor === planDate) return;

    const next: Record<number, RowDraft> = {};
    // Defaults: всі 24 години з power_min, V_eko=0, forecast=""
    for (let h = 0; h < 24; h++) {
      next[h] = {
        power_kw: String(Math.round(powerMin)),
        v_eko: "0",
        forecast: "",
      };
    }
    // Перезапис з plan_hours.
    for (const ph of planQ.data?.hours ?? []) {
      const eko = Number(ph.volumes.ekosfera ?? 0);
      next[ph.hour] = {
        power_kw: String(Math.round(Number(ph.power_kw))),
        v_eko: String(Math.round(eko)),
        forecast: next[ph.hour]?.forecast ?? "",
      };
    }
    // Forecast'и з окремої таблиці.
    for (const f of forecastQ.data ?? []) {
      if (f.client_code !== "ekosfera") continue;
      next[f.hour] = {
        ...(next[f.hour] ?? { power_kw: String(powerMin), v_eko: "0", forecast: "" }),
        forecast: String(Math.round(Number(f.forecast_kwh))),
      };
    }
    setRows(next);
    setLoadedFor(planDate);
  }, [planQ.data, forecastQ.data, planDate, loadedFor, powerMin]);

  const reloadFromBackend = () => setLoadedFor(null);

  const setHour = (hour: number, patch: Partial<RowDraft>) => {
    setRows((s) => ({
      ...s,
      [hour]: { ...(s[hour] ?? { power_kw: "", v_eko: "", forecast: "" }), ...patch },
    }));
  };

  // ─── Derived values per row ───
  function calcFreeRdn(P: number, V_eko: number): { free: number; v_rdn: number; deficit: number } {
    const free = Math.max(0, P - selfNeedsApplied);
    const allocated_eko = Math.min(V_eko, free);
    const v_rdn = Math.max(0, free - allocated_eko);
    const deficit = Math.max(0, V_eko - free);
    return { free, v_rdn, deficit };
  }

  // ─── Save mutations ───
  const saveHour = useMutation({
    mutationFn: async ({ hour, draft }: { hour: number; draft: RowDraft }) => {
      const P = Number(draft.power_kw || "0");
      const V_eko = Number(draft.v_eko || "0");
      const { v_rdn } = calcFreeRdn(P, V_eko);
      // 1) Зберігаємо plan_hour
      await plansApi.putHour(planDate, hour, {
        power_kw: P,
        volumes: {
          ekosfera: Math.min(V_eko, Math.max(0, P - selfNeedsApplied)),
          rdn: v_rdn,
        },
      });
      // 2) Зберігаємо/видаляємо прогноз ЕкоСфери
      const f = draft.forecast.trim();
      if (f === "") {
        await forecastsApi.delete(planDate, hour, "ekosfera").catch(() => undefined);
      } else {
        await forecastsApi.put(planDate, hour, "ekosfera", Number(f));
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["plans", planDate] });
      void qc.invalidateQueries({ queryKey: ["forecasts", planDate] });
      void qc.invalidateQueries({ queryKey: ["metrics-hourly", planDate] });
      void qc.invalidateQueries({ queryKey: ["metrics-daily-h1"] });
    },
  });

  const copyFromMutation = useMutation({
    mutationFn: (source: "yesterday" | "weekly_avg") =>
      plansApi.copyFrom(planDate, source),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["plans", planDate] });
      reloadFromBackend();
    },
  });

  const submitMutation = useMutation({
    mutationFn: () => plansApi.submit(planDate),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["plans", planDate] }),
  });

  const applyBestAll = () => {
    if (!optQ.data) return;
    setRows((s) => {
      const next = { ...s };
      for (const h of optQ.data!.hours) {
        const cur = next[h.hour] ?? { power_kw: "", v_eko: "0", forecast: "" };
        next[h.hour] = { ...cur, power_kw: String(Math.round(h.best.power_kw)) };
      }
      return next;
    });
  };

  // ─── Totals ───
  const totals = useMemo(() => {
    let totalP = 0, totalEko = 0, totalRdn = 0, totalDeficit = 0;
    for (let h = 0; h < 24; h++) {
      const r = rows[h];
      if (!r) continue;
      const P = Number(r.power_kw || "0");
      const V_eko = Number(r.v_eko || "0");
      const { v_rdn, deficit } = calcFreeRdn(P, V_eko);
      totalP += P;
      totalEko += Math.min(V_eko, Math.max(0, P - selfNeedsApplied));
      totalRdn += v_rdn;
      totalDeficit += deficit;
    }
    return { totalP, totalEko, totalRdn, totalDeficit };
  }, [rows, selfNeedsApplied]);

  // ─── Render ───
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{t("plan2.title")}</CardTitle>
          <CardDescription>{t("plan2.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          {/* Date with arrows */}
          <div className="space-y-1">
            <Label>{t("plan.date")}</Label>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPlanDate((d) => shiftDate(d, -1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Input
                type="date"
                value={planDate}
                onChange={(e) => setPlanDate(e.target.value)}
                className="w-[160px]"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPlanDate((d) => shiftDate(d, 1))}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Self-needs toggle */}
          <div className="space-y-1">
            <Label>{t("plan2.self_needs_label")}</Label>
            <button
              type="button"
              onClick={() => setCountSelfNeeds((v) => !v)}
              title={t("plan2.self_needs_hint", { kwh: selfNeedsKw })}
              className={cn(
                "rounded-md border px-3 py-1.5 text-xs font-medium transition-colors",
                countSelfNeeds
                  ? "border-brand-mint-300 bg-brand-mint-50 text-brand-mint-700"
                  : "border-slate-200 bg-white text-slate-500",
              )}
            >
              {countSelfNeeds ? "✓ " : ""}
              {t("plan2.self_needs_toggle", { kwh: selfNeedsKw })}
            </button>
          </div>

          {/* Action buttons */}
          {!readonly && (
            <div className="space-y-1 flex flex-row gap-2 items-end">
              <Button
                variant="outline"
                size="sm"
                disabled={copyFromMutation.isPending}
                onClick={() => copyFromMutation.mutate("yesterday")}
              >
                <Copy className="h-4 w-4" /> {t("plan.copy_yesterday")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={copyFromMutation.isPending}
                onClick={() => copyFromMutation.mutate("weekly_avg")}
              >
                <Calendar className="h-4 w-4" /> {t("plan.copy_weekly_avg")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!optQ.data}
                onClick={applyBestAll}
              >
                <Sparkles className="h-4 w-4" /> {t("plan2.apply_best_all")}
              </Button>
            </div>
          )}

          {/* Submit */}
          {canEdit && (
            <div className="ml-auto">
              <Button
                variant={submitted ? "outline" : "default"}
                disabled={submitted || submitMutation.isPending}
                onClick={() => {
                  if (window.confirm(t("plan.submit_confirm"))) submitMutation.mutate();
                }}
              >
                <Send className="h-4 w-4" />
                {submitted
                  ? t("plan.submitted_banner", { time: new Date(planQ.data?.submitted_at ?? "").toLocaleTimeString("uk-UA") })
                  : t("plan.submit")}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {submitted && (
        <div className="rounded-md bg-emerald-50 border border-emerald-300 p-3 text-sm text-emerald-900">
          {t("plan.submitted_banner", {
            time: new Date(planQ.data!.submitted_at!).toLocaleTimeString("uk-UA"),
          })}
        </div>
      )}

      {/* Totals strip */}
      <Card>
        <CardContent className="flex flex-wrap gap-6 py-3 text-sm">
          <div>
            <span className="text-slate-500">Σ P · 1год: </span>
            <span className="font-mono font-medium">{fmt(totals.totalP)} кВт·год</span>
          </div>
          <div>
            <span className="text-slate-500">Σ ЕкоСфера: </span>
            <span className="font-mono font-medium">{fmt(totals.totalEko)} кВт·год</span>
          </div>
          <div>
            <span className="text-slate-500">Σ РДН: </span>
            <span className="font-mono font-medium">{fmt(totals.totalRdn)} кВт·год</span>
          </div>
          {totals.totalDeficit > 0 && (
            <div className="text-red-700">
              ⚠ Дефіцит ЕкоСфера: <span className="font-mono">{fmt(totals.totalDeficit)}</span> кВт·год
            </div>
          )}
        </CardContent>
      </Card>

      {/* Hours table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-slate-500">
                <th className="p-2 w-12">{t("common.hour")}</th>
                <th className="p-2 w-28">{t("optimizer.rdn_price")}</th>
                <th className="p-2 w-40">{t("plan.power_kw")}</th>
                <th className="p-2 w-24 text-right">{t("home1.volume_ekosfera")}</th>
                <th className="p-2 w-24 text-right">{t("plan2.forecast_ekosfera")}</th>
                <th className="p-2 w-24 text-right">{t("home1.volume_rdn")}</th>
                <th className="p-2 w-32 text-right">{t("plan2.your_profit")}</th>
                <th className="p-2 w-32 text-right">{t("plan2.best")}</th>
                <th className="p-2 w-24 text-right">{t("plan2.delta")}</th>
                <th className="p-2 w-20" />
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 24 }, (_, h) => h).map((h) => (
                <HourRow
                  key={h}
                  hour={h}
                  draft={rows[h]}
                  onChange={(patch) => setHour(h, patch)}
                  onSave={() => rows[h] && saveHour.mutate({ hour: h, draft: rows[h] })}
                  saving={saveHour.isPending}
                  readonly={readonly}
                  optimizer={optQ.data}
                  planDate={planDate}
                  powerMin={powerMin}
                  powerMax={powerMax}
                  powerStep={powerStep}
                  selfNeedsApplied={selfNeedsApplied}
                  ekoPrice={Number(ekoClient?.tariff_type === "fixed" ? 9.5 : 0)} // fallback; точне з backend через quick-calc
                  baselineEko={baselineEkoByHour.get(h) ?? null}
                />
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// ───────────────────────────── Row ────────────────────────────

interface HourRowProps {
  hour: number;
  draft: RowDraft | undefined;
  onChange: (patch: Partial<RowDraft>) => void;
  onSave: () => void;
  saving: boolean;
  readonly: boolean;
  optimizer: DayOptimum | undefined;
  planDate: string;
  powerMin: number;
  powerMax: number;
  powerStep: number;
  selfNeedsApplied: number;
  ekoPrice: number;
  baselineEko: number | null;
}

function HourRow({
  hour, draft, onChange, onSave, saving, readonly,
  optimizer, planDate, powerMin, powerMax, powerStep, selfNeedsApplied,
  baselineEko,
}: HourRowProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const opt = optimizer?.hours.find((h) => h.hour === hour);
  const P = Number(draft?.power_kw || "0");
  const V_eko_input = Number(draft?.v_eko || "0");
  const free = Math.max(0, P - selfNeedsApplied);
  const V_eko = Math.min(V_eko_input, free);
  const V_rdn = Math.max(0, free - V_eko);
  const deficit = Math.max(0, V_eko_input - free);

  // ─── Live quick-calc (debounced) ───
  const [calc, setCalc] = useState<QuickCalcOut | null>(null);
  const [calcLoading, setCalcLoading] = useState(false);

  const runQuickCalc = useCallback(async () => {
    if (P <= 0) {
      setCalc(null);
      return;
    }
    setCalcLoading(true);
    try {
      const result = await optimizerApi.quickCalc({
        date: planDate,
        hour,
        power_kw: P,
        volumes: { ekosfera: V_eko, rdn: V_rdn },
        count_self_needs: selfNeedsApplied > 0,
      });
      setCalc(result);
    } catch {
      setCalc(null);
    } finally {
      setCalcLoading(false);
    }
  }, [P, V_eko, V_rdn, planDate, hour, selfNeedsApplied]);

  useEffect(() => {
    const handle = setTimeout(runQuickCalc, 300);
    return () => clearTimeout(handle);
  }, [runQuickCalc]);

  const yourProfit = calc?.profit_uah ?? null;
  const bestProfit = opt?.best.profit_uah ?? null;
  const delta = yourProfit !== null && bestProfit !== null ? yourProfit - bestProfit : null;

  return (
    <>
      <tr className={cn("border-t border-slate-100", deficit > 0 && "bg-red-50/40")}>
        <td className="p-2 font-mono text-xs">{String(hour).padStart(2, "0")}:00</td>
        <td className="p-2 text-xs">
          {opt?.rdn_price_uah_per_mwh !== null && opt?.rdn_price_uah_per_mwh !== undefined ? (
            <span className="inline-flex items-center gap-1">
              {Math.round(opt.rdn_price_uah_per_mwh).toLocaleString("uk-UA")}
              {opt.rdn_price_is_forecast && (
                <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-medium text-amber-700">
                  {t("optimizer.rdn_price_forecast_badge")}
                </span>
              )}
            </span>
          ) : (
            <span className="text-slate-400">—</span>
          )}
        </td>
        <td className="p-2">
          <div className="flex flex-col gap-1">
            <Input
              type="number"
              step={powerStep}
              min={powerMin}
              max={powerMax}
              value={draft?.power_kw ?? ""}
              onChange={(e) => onChange({ power_kw: e.target.value })}
              disabled={readonly}
              className="h-8 text-xs"
            />
            <input
              type="range"
              min={powerMin}
              max={powerMax}
              step={powerStep}
              value={draft?.power_kw ?? powerMin}
              onChange={(e) => onChange({ power_kw: e.target.value })}
              disabled={readonly}
              className="w-full accent-brand-mint-500"
            />
          </div>
        </td>
        <td className="p-2 text-right">
          <Input
            type="number"
            step="10"
            min={0}
            value={draft?.v_eko ?? ""}
            onChange={(e) => onChange({ v_eko: e.target.value })}
            disabled={readonly}
            className="h-8 text-xs w-full text-right"
          />
          {deficit > 0 && (
            <div className="text-[10px] text-red-600">−{Math.round(deficit)}</div>
          )}
        </td>
        <td className="p-2 text-right">
          <Input
            type="number"
            step="10"
            min={0}
            value={draft?.forecast ?? ""}
            onChange={(e) => onChange({ forecast: e.target.value })}
            disabled={readonly}
            className="h-8 text-xs w-full text-right"
            placeholder={baselineEko !== null ? `~ ${Math.round(baselineEko)}` : "—"}
          />
          {baselineEko !== null && (
            <div
              className="text-[10px] text-slate-400 mt-0.5 text-right"
              title={t("plan2.baseline_hint")}
            >
              ~ {Math.round(baselineEko)}
            </div>
          )}
        </td>
        <td className="p-2 text-right font-mono text-xs text-slate-600">
          {Math.round(V_rdn).toLocaleString("uk-UA")}
        </td>
        <td className="p-2 text-right font-mono text-xs">
          {calcLoading && yourProfit === null ? (
            <span className="text-slate-400">…</span>
          ) : yourProfit !== null ? (
            <span className={yourProfit >= 0 ? "text-emerald-700" : "text-red-700"}>
              {yourProfit >= 0 ? "+" : ""}
              {Math.round(yourProfit).toLocaleString("uk-UA")}
            </span>
          ) : (
            <span className="text-slate-400">—</span>
          )}
        </td>
        <td className="p-2 text-right font-mono text-xs">
          {opt ? (
            <span className="text-slate-600">
              {Math.round(opt.best.power_kw)} /{" "}
              <span className={opt.best.profit_uah >= 0 ? "text-emerald-700" : "text-red-700"}>
                {opt.best.profit_uah >= 0 ? "+" : ""}
                {Math.round(opt.best.profit_uah).toLocaleString("uk-UA")}
              </span>
            </span>
          ) : (
            <span className="text-slate-400">—</span>
          )}
        </td>
        <td className="p-2 text-right font-mono text-xs">
          {delta !== null ? (
            <span className={delta >= 0 ? "text-emerald-700" : "text-red-700"}>
              {delta >= 0 ? "+" : ""}
              {Math.round(delta).toLocaleString("uk-UA")}
            </span>
          ) : (
            <span className="text-slate-400">—</span>
          )}
        </td>
        <td className="p-2">
          <div className="flex items-center gap-1">
            {!readonly && (
              <Button size="sm" variant="outline" onClick={onSave} disabled={saving} className="h-7 px-2">
                <Save className="h-3 w-3" />
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setExpanded((v) => !v)}
              className="h-7 px-1"
            >
              {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </Button>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-slate-50/40 border-b border-slate-200">
          <td colSpan={10} className="p-4">
            <ExpandedDetails
              hour={hour}
              date={planDate}
              calc={calc}
              opt={opt}
              powerMin={powerMin}
              powerMax={powerMax}
              currentP={P}
              onApplyBest={() => {
                if (opt && !readonly) {
                  onChange({ power_kw: String(Math.round(opt.best.power_kw)) });
                }
              }}
            />
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Expanded row: charts + breakdown ───

interface ExpandedProps {
  hour: number;
  date: string;
  calc: QuickCalcOut | null;
  opt: DayOptimum["hours"][number] | undefined;
  powerMin: number;
  powerMax: number;
  currentP: number;
  onApplyBest: () => void;
}

function ExpandedDetails({
  hour, date, calc, opt, powerMin, powerMax, currentP, onApplyBest,
}: ExpandedProps) {
  const { t } = useTranslation();
  const detailQ = useQuery({
    queryKey: ["optimizer", "hour", date, hour],
    queryFn: () => optimizerApi.hour(date, hour),
    retry: 0,
  });
  const detail = detailQ.data;

  const chartData = useMemo(() => {
    if (!detail) return [];
    return detail.scenarios.map((s) => ({
      power_kw: s.power_kw,
      profit_uah: s.profit_uah,
      cost_per_mwh: s.cost_uah_per_mwh,
    }));
  }, [detail]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Profit vs Power chart */}
        <div className="h-56 bg-white rounded border border-slate-200 p-2">
          <div className="text-xs text-slate-500 mb-1">{t("optimizer.chart_title_profit")}</div>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="90%">
              <LineChart data={chartData} margin={{ top: 5, right: 10, bottom: 15, left: 35 }}>
                <CartesianGrid stroke="#e2e8f0" />
                <XAxis dataKey="power_kw" type="number" domain={[powerMin, powerMax]} />
                <YAxis
                  width={65}
                  domain={[
                    (m: number) => Math.floor(m - Math.abs(m) * 0.1),
                    (m: number) => Math.ceil(m + Math.abs(m) * 0.1),
                  ]}
                />
                <Tooltip />
                <Line dataKey="profit_uah" stroke="#3ec79a" strokeWidth={2} dot={false} />
                {opt && (
                  <ReferenceDot
                    x={opt.best.power_kw}
                    y={opt.best.profit_uah}
                    r={5}
                    fill="#002060"
                    stroke="#fff"
                    strokeWidth={2}
                  />
                )}
                <ReferenceLine x={currentP} stroke="#94a3b8" strokeDasharray="3 3" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-xs text-slate-400">{t("common.loading")}</div>
          )}
        </div>
        {/* Cost vs Power chart */}
        <div className="h-56 bg-white rounded border border-slate-200 p-2">
          <div className="text-xs text-slate-500 mb-1">{t("optimizer.chart_title_cost")}</div>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="90%">
              <LineChart data={chartData} margin={{ top: 5, right: 10, bottom: 15, left: 35 }}>
                <CartesianGrid stroke="#e2e8f0" />
                <XAxis dataKey="power_kw" type="number" domain={[powerMin, powerMax]} />
                <YAxis
                  width={65}
                  domain={[
                    (m: number) => Math.floor(m * 0.95),
                    (m: number) => Math.ceil(m * 1.05),
                  ]}
                />
                <Tooltip />
                <Line dataKey="cost_per_mwh" stroke="#002060" strokeWidth={2} dot={false} />
                <ReferenceLine x={currentP} stroke="#94a3b8" strokeDasharray="3 3" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-xs text-slate-400">{t("common.loading")}</div>
          )}
        </div>
      </div>

      {/* Breakdown */}
      {calc && (
        <div className="text-xs text-slate-600 grid grid-cols-2 gap-x-8 gap-y-1 font-mono">
          <div>self_needs = {fmt(calc.energy.self_needs_kwh)} кВт·год</div>
          <div>free = {fmt(calc.energy.free_kwh)} кВт·год</div>
          <div>used = {fmt(calc.energy.used_kwh)} кВт·год</div>
          <div>leftover = {fmt(calc.energy.leftover_kwh)} кВт·год</div>
          <div>rdn_price = {calc.rdn_price_uah_per_mwh !== null ? fmt(calc.rdn_price_uah_per_mwh) : "—"} грн/МВт·год</div>
          <div>eko_tariff = {calc.eko_tariff_uah_per_kwh ? fmt(calc.eko_tariff_uah_per_kwh, 4) : "—"} грн/кВт·год</div>
          <div>revenue = {fmt(calc.revenue.total_uah)} грн</div>
          <div>cost/MWh = {fmt(calc.cost.cost_uah_per_mwh)} грн</div>
          <div className="col-span-2 border-t border-slate-200 pt-1 mt-1">
            profit = {fmt(num(calc.profit_uah))} грн
          </div>
          {calc.warnings.length > 0 && (
            <div className="col-span-2 text-amber-700">⚠ {calc.warnings.join(", ")}</div>
          )}
        </div>
      )}

      {/* Apply best button */}
      {opt && (
        <Button size="sm" variant="outline" onClick={onApplyBest}>
          <Sparkles className="h-3 w-3" /> {t("plan2.apply_best_hour", { p: Math.round(opt.best.power_kw) })}
        </Button>
      )}
    </div>
  );
}
